/**
 * Preorder Badge Enhancement
 * Ensures preorder badges are displayed correctly on product cards
 * by providing a JavaScript fallback for metafield reading
 */

class PreorderBadgeManager {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.enhanceBadges());
    } else {
      this.enhanceBadges();
    }

    // Also run when new products are loaded (for infinite scroll, etc.)
    document.addEventListener('shopify:section:load', () => this.enhanceBadges());
  }

  enhanceBadges() {
    // Find all product cards
    const productCards = document.querySelectorAll('.product-card');
    
    productCards.forEach((card, index) => {
      this.processProductCard(card);
    });
  }

  async processProductCard(card) {
    try {
      const productId = card.dataset.productId;
      const productHandle = card.dataset.productHandle;
      const stockType = card.dataset.stockType;
      
      // Check if badge already exists
      const existingBadge = card.querySelector('.badge--preorder');
      
      if (existingBadge) {
        return; // Badge already exists, no need to add
      }

      // First check the data attribute from Liquid template
      const isPreorderFromData = stockType === 'preorder';
      
      if (isPreorderFromData) {
        this.addPreorderBadge(card);
        return;
      }

      // Fallback: Try to get product data from API (though we know metafields won't be there)
      const productData = await this.getProductData(card);
      
      if (productData) {
        // Check if product is preorder
        const isPreorder = this.isProductPreorder(productData);
        
        if (isPreorder) {
          this.addPreorderBadge(card);
        }
      }
    } catch (error) {
      console.error('Error processing product card:', error);
    }
  }

  async getProductData(card) {
    // Method 1: Use data attributes we added to the card
    const productId = card.dataset.productId;
    const productHandle = card.dataset.productHandle;
    


    // Method 2: Try to find product data in nearby script tags
    const productScript = card.querySelector('script[type="application/json"]');
    if (productScript) {
      try {
        const data = JSON.parse(productScript.textContent);
        return data;
      } catch (e) {
        // Failed to parse JSON, continue to other methods
      }
    }

    // Method 3: Try to fetch product data if we have a handle
    if (productHandle) {
      const productData = await this.fetchProductData(productHandle);
      if (productData) {
        return productData;
      }
    }

    // Method 4: Try to extract product handle from URL
    const productLink = card.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      const handleMatch = href.match(/\/products\/([^?#]+)/);
      if (handleMatch) {
        const handle = handleMatch[1];
        const productData = await this.fetchProductData(handle);
        if (productData) {
          return productData;
        }
        return { handle: handle };
      }
    }

    // Method 5: Return basic data from attributes
    if (productId) {
      return { id: productId, handle: productHandle };
    }

    return null;
  }

  isProductPreorder(productData) {
    
    // Check various metafield access patterns
    const metafields = productData.metafields;
    if (!metafields) {
      return false;
    }


    // Pattern 1: metafields.ActionFigure.stock_type
    if (metafields.ActionFigure?.stock_type) {
      const stockType = metafields.ActionFigure.stock_type.value || metafields.ActionFigure.stock_type;
      const normalized = this.normalizeStockType(stockType);
      if (normalized === 'preorder') return true;
    }

    // Pattern 2: metafields['ActionFigure']['stock_type']
    if (metafields['ActionFigure']?.['stock_type']) {
      const stockType = metafields['ActionFigure']['stock_type'].value || metafields['ActionFigure']['stock_type'];
      const normalized = this.normalizeStockType(stockType);
      if (normalized === 'preorder') return true;
    }

    // Pattern 3: Check if any metafield contains preorder info
    for (const namespace in metafields) {
      const fields = metafields[namespace];
      if (typeof fields === 'object') {
        for (const key in fields) {
          if (key.toLowerCase().includes('stock') || key.toLowerCase().includes('type')) {
            const value = fields[key].value || fields[key];
            const normalized = this.normalizeStockType(value);
            if (normalized === 'preorder') {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  normalizeStockType(value) {
    if (!value) return '';
    return String(value).toLowerCase().trim();
  }

  addPreorderBadge(card) {
    
    // Find the badges container
    let badgesContainer = card.querySelector('.badges');
    
    if (!badgesContainer) {
      // Create badges container if it doesn't exist
      const mediaContainer = card.querySelector('.product-card__media');
      
      if (!mediaContainer) {
        return;
      }

      badgesContainer = document.createElement('div');
      badgesContainer.className = 'badges z-2 absolute grid gap-3 pointer-events-none';
      mediaContainer.appendChild(badgesContainer);
    }

    // Create preorder badge
    const badge = document.createElement('span');
    badge.className = 'badge badge--preorder flex items-center gap-1d5 font-medium leading-none rounded-full';
    badge.textContent = this.getPreorderText();
    

    // Add to container
    badgesContainer.appendChild(badge);
  }

  getPreorderText() {
    // Try to get localized text
    if (window.theme?.strings?.preorder) {
      return window.theme.strings.preorder;
    }
    
    // Fallback to English
    return 'Preorder';
  }

  // Async method to fetch product data if needed
  async fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      // Silently fail - this is expected in most cases
    }
    return null;
  }
}

// Initialize the preorder badge manager
new PreorderBadgeManager();

// Export for potential use by other scripts
window.PreorderBadgeManager = PreorderBadgeManager;
